import React, { forwardRef } from 'react';
import { cn } from '@/utils/cn';

type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'accent'
  | 'outline'
  | 'ghost'
  | 'destructive'
  | 'success'
  | 'link';

type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
  children: React.ReactNode;
}

const variantClasses: Record<ButtonVariant, string> = {
  primary: cn(
    'bg-primary-500 text-white shadow-md',
    'hover:bg-primary-600 hover:shadow-lg',
    'active:bg-primary-700',
    'dark:bg-primary-600 dark:hover:bg-primary-500',
    'focus-visible:ring-primary-500'
  ),
  secondary: cn(
    'bg-secondary-500 text-white shadow-md',
    'hover:bg-secondary-600 hover:shadow-lg',
    'active:bg-secondary-700',
    'dark:bg-secondary-600 dark:hover:bg-secondary-500',
    'focus-visible:ring-secondary-500'
  ),
  accent: cn(
    'bg-accent-500 text-dark-900 font-semibold shadow-md',
    'hover:bg-accent-400 hover:shadow-lg hover:shadow-accent-500/20',
    'active:bg-accent-600',
    'focus-visible:ring-accent-500'
  ),
  outline: cn(
    'border-2 border-gray-300 bg-transparent text-gray-700',
    'hover:bg-gray-100 hover:border-gray-400',
    'dark:border-dark-600 dark:text-gray-300',
    'dark:hover:bg-dark-800 dark:hover:border-dark-500',
    'focus-visible:ring-gray-500'
  ),
  ghost: cn(
    'text-gray-700 bg-transparent',
    'hover:bg-gray-100',
    'dark:text-gray-300 dark:hover:bg-dark-800',
    'focus-visible:ring-gray-500'
  ),
  destructive: cn(
    'bg-error-500 text-white shadow-md',
    'hover:bg-error-600 hover:shadow-lg',
    'active:bg-error-700',
    'focus-visible:ring-error-500'
  ),
  success: cn(
    'bg-success-500 text-white shadow-md',
    'hover:bg-success-600 hover:shadow-lg',
    'active:bg-success-700',
    'focus-visible:ring-success-500'
  ),
  link: cn(
    'text-primary-600 underline-offset-4',
    'hover:underline',
    'dark:text-primary-400',
    'focus-visible:ring-primary-500'
  ),
};

const sizeClasses: Record<ButtonSize, string> = {
  xs: 'h-7 px-2.5 text-xs rounded-md gap-1',
  sm: 'h-9 px-3 text-sm rounded-lg gap-1.5',
  md: 'h-10 px-4 text-sm rounded-lg gap-2',
  lg: 'h-12 px-6 text-base rounded-lg gap-2',
  xl: 'h-14 px-8 text-lg rounded-xl gap-2.5',
};

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      leftIcon,
      rightIcon,
      fullWidth = false,
      className,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const baseClasses = cn(
      'inline-flex items-center justify-center font-medium',
      'transition-all duration-200 ease-out',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
      'disabled:opacity-50 disabled:pointer-events-none disabled:cursor-not-allowed',
      'active:scale-[0.98]',
      'dark:ring-offset-dark-900'
    );

    const LoadingSpinner = () => (
      <svg
        className="animate-spin h-4 w-4"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    );

    return (
      <button
        ref={ref}
        className={cn(
          baseClasses,
          variantClasses[variant],
          sizeClasses[size],
          fullWidth && 'w-full',
          className
        )}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? (
          <>
            <LoadingSpinner />
            <span>Loading...</span>
          </>
        ) : (
          <>
            {leftIcon && <span className="flex-shrink-0">{leftIcon}</span>}
            {children}
            {rightIcon && <span className="flex-shrink-0">{rightIcon}</span>}
          </>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';

// Icon Button variant
interface IconButtonProps extends Omit<ButtonProps, 'leftIcon' | 'rightIcon' | 'children'> {
  icon: React.ReactNode;
  'aria-label': string;
}

const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ icon, size = 'md', className, ...props }, ref) => {
    const iconSizeClasses: Record<ButtonSize, string> = {
      xs: 'h-7 w-7',
      sm: 'h-9 w-9',
      md: 'h-10 w-10',
      lg: 'h-12 w-12',
      xl: 'h-14 w-14',
    };

    return (
      <Button
        ref={ref}
        size={size}
        className={cn(iconSizeClasses[size], 'p-0', className)}
        {...props}
      >
        {icon}
      </Button>
    );
  }
);

IconButton.displayName = 'IconButton';

export { Button, IconButton };
export default Button;
